import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ClipboardCheck, LogIn } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Bienvenue, ${user.name} !`);
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'consultant') {
        navigate('/consultant');
      } else {
        navigate('/pme');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de la connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Formulaire */}
      <div className="flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_consult-tracker-14/artifacts/zvf1f82s_logo-ccitogo.png" 
              alt="CCI Togo" 
              className="h-12 w-auto"
            />
            <span className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CCI Togo</span>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-semibold text-slate-900">Connexion</CardTitle>
              <CardDescription className="text-slate-500">
                Entrez vos identifiants pour accéder à votre tableau de bord
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@entreprise.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="h-11 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700 font-medium">Mot de passe</Label>
                    <Link 
                      to="/forgot-password" 
                      className="text-sm text-orange-500 hover:text-orange-600 font-medium"
                      data-testid="forgot-password-link"
                    >
                      Mot de passe oublié ?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-11 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Connexion...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <LogIn className="w-4 h-4" />
                      Se connecter
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-slate-600">
            Pas encore de compte ?{' '}
            <Link to="/register" className="text-orange-500 hover:text-orange-600 font-medium" data-testid="register-link">
              Créer un compte
            </Link>
          </p>
        </div>
      </div>

      {/* Image */}
      <div 
        className="hidden lg:block bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/27277185/pexels-photo-27277185.jpeg)' }}
      >
        <div className="absolute inset-0 bg-slate-900/60 flex items-end p-12">
          <div className="text-white max-w-lg">
            <h2 className="text-3xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Suivez vos interventions en toute confiance
            </h2>
            <p className="text-slate-200 text-lg leading-relaxed">
              Optimisez vos flux de travail de conseil aux PME. Les consultants enregistrent leurs interventions, 
              les PME valident et donnent leur avis — le tout en un seul endroit.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
